import type React from "react"
import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import {
  isCompleteInclusivePickerRange,
  pickerBoundsForPartialRange,
} from "@/utils/date"
import type { DateTime } from "luxon"

import type { PickerChangeHandlerContext } from "@mui/x-date-pickers-pro"
import { AdapterLuxon } from "@mui/x-date-pickers-pro/AdapterLuxon"
import { DateRangePicker } from "@mui/x-date-pickers-pro/DateRangePicker"
import { LocalizationProvider } from "@mui/x-date-pickers-pro/LocalizationProvider"
import type {
  DateRange,
  DateRangeValidationError,
} from "@mui/x-date-pickers-pro/models"

export interface DateRangeFieldShortcut {
  disabled?: boolean
  getValue: () => DateRange<DateTime>
  label: string
}

interface DateRangeFieldProps {
  maxDate?: DateTime | null
  minDate?: DateTime | null
  onChange: (range: DateRange<DateTime>) => void
  shortcuts: readonly DateRangeFieldShortcut[]
  value: DateRange<DateTime>
}

interface DraftPickerState {
  committedKey: string
  value: DateRange<DateTime>
}

interface LatestPicker {
  validationError: DateRangeValidationError | null
  value: DateRange<DateTime>
}

interface PublishDraftRangeOptions {
  committed: DateRange<DateTime>
  next: DateRange<DateTime>
  onChange: (range: DateRange<DateTime>) => void
  publishedKeyRef: { current: string | null }
  setDraftPicker: (draft: DraftPickerState | null) => void
  validationError: DateRangeValidationError | null
}

const toIsoDate = (value: DateTime | null | undefined): string | null =>
  value?.isValid ? value.toISODate() : null

const toCommittedKey = (value: DateRange<DateTime>): string =>
  `${toIsoDate(value[0]) ?? ""}:${toIsoDate(value[1]) ?? ""}`

const isSamePickerDays = (
  left: DateRange<DateTime>,
  right: DateRange<DateTime>
): boolean => toCommittedKey(left) === toCommittedKey(right)

const isInvertedPickerRange = (value: DateRange<DateTime>): boolean => {
  const start = toIsoDate(value[0])
  const end = toIsoDate(value[1])
  return start != null && end != null && start > end
}

const hasDateRangeValidationError = (
  error: DateRangeValidationError | null
): boolean => error?.some((entry) => entry !== null) ?? false

const isFocusRemainingInField = (event: React.FocusEvent): boolean => {
  const nextTarget = event.relatedTarget
  if (!(nextTarget instanceof Node)) {
    return false
  }

  if (event.currentTarget.contains(nextTarget)) {
    return true
  }

  const field = event.currentTarget.closest("[role='group']")
  return field?.contains(nextTarget) ?? false
}

const getNextDraftPicker = (
  current: DraftPickerState | null,
  committedKey: string,
  next: DateRange<DateTime>
): DraftPickerState => {
  if (
    current?.committedKey === committedKey &&
    toCommittedKey(current.value) === toCommittedKey(next)
  ) {
    return current
  }
  return { committedKey, value: next }
}

const publishDraftRange = ({
  committed,
  next,
  onChange,
  publishedKeyRef,
  setDraftPicker,
  validationError,
}: PublishDraftRangeOptions): void => {
  if (
    hasDateRangeValidationError(validationError) ||
    isInvertedPickerRange(next)
  ) {
    return
  }

  const pickerRange = { end: next[1], start: next[0] }
  if (!isCompleteInclusivePickerRange(pickerRange)) {
    return
  }

  if (isSamePickerDays(next, committed)) {
    setDraftPicker(null)
    return
  }

  const publishedKey = toCommittedKey(next)
  if (publishedKeyRef.current === publishedKey) {
    return
  }

  publishedKeyRef.current = publishedKey
  onChange([pickerRange.start, pickerRange.end])
}

const publishShortcutDraftRange = (
  shortcut: PickerChangeHandlerContext<DateRangeValidationError>["shortcut"],
  options: PublishDraftRangeOptions
): void => {
  if (!shortcut) {
    return
  }
  publishDraftRange(options)
}

const publishOnFieldBlur = (
  event: React.FocusEvent,
  isPickerOpen: boolean,
  publish: () => void
): void => {
  if (isPickerOpen || isFocusRemainingInField(event)) {
    return
  }
  publish()
}

const isDateSectionTarget = (target: EventTarget | null): boolean =>
  target instanceof HTMLElement && target.getAttribute("role") === "spinbutton"

const publishOnFieldKey = (
  event: React.KeyboardEvent,
  isPickerOpen: boolean,
  publish: () => void
): void => {
  if (
    isPickerOpen ||
    !isDateSectionTarget(event.target) ||
    (event.key !== "Enter" && event.key !== "Tab")
  ) {
    return
  }
  if (event.key === "Enter") {
    event.preventDefault()
  }
  publish()
}

/**
 * Shared inclusive date-range field used by site bills and interval data.
 * A range is published on blur, Enter, picker close, or a shortcut, and only
 * when both days are valid and the start is not after the end.
 */
export const DateRangeField: React.FC<DateRangeFieldProps> = ({
  maxDate = null,
  minDate = null,
  onChange,
  shortcuts,
  value,
}) => {
  const { i18n, t } = useTranslation("sites")
  const committedKey = toCommittedKey(value)
  const [draftPicker, setDraftPicker] = useState<DraftPickerState | null>(null)
  const pickerValue =
    draftPicker?.committedKey === committedKey ? draftPicker.value : value
  const isPickerOpenRef = useRef(false)
  const latestPickerRef = useRef<LatestPicker>({
    validationError: null,
    value: pickerValue,
  })
  const publishedKeyRef = useRef<string | null>(null)
  const seenCommittedKeyRef = useRef(committedKey)
  useEffect(() => {
    if (seenCommittedKeyRef.current === committedKey) {
      return
    }
    seenCommittedKeyRef.current = committedKey
    publishedKeyRef.current = null
    latestPickerRef.current = {
      validationError: null,
      value,
    }
  }, [committedKey, value])
  if (draftPicker !== null && draftPicker.committedKey !== committedKey) {
    setDraftPicker(null)
  }
  const [pickerStart, pickerEnd] = pickerValue
  const pickerBounds =
    minDate?.isValid && maxDate?.isValid
      ? pickerBoundsForPartialRange({
          end: pickerEnd,
          maxDate,
          minDate,
          start: pickerStart,
        })
      : { maxDate, minDate }

  const publishLatestDraft = () => {
    publishDraftRange({
      committed: value,
      next: latestPickerRef.current.value,
      onChange,
      publishedKeyRef,
      setDraftPicker,
      validationError: latestPickerRef.current.validationError,
    })
  }

  const handleDateRangeChange = (
    next: DateRange<DateTime>,
    context: PickerChangeHandlerContext<DateRangeValidationError>
  ) => {
    latestPickerRef.current = {
      validationError: context.validationError,
      value: next,
    }
    setDraftPicker((current) => getNextDraftPicker(current, committedKey, next))
    publishShortcutDraftRange(context.shortcut, {
      committed: value,
      next,
      onChange,
      publishedKeyRef,
      setDraftPicker,
      validationError: context.validationError,
    })
  }

  const handleFieldBlur = (event: React.FocusEvent) => {
    publishOnFieldBlur(event, isPickerOpenRef.current, publishLatestDraft)
  }

  const handleFieldKeyDown = (event: React.KeyboardEvent) => {
    publishOnFieldKey(event, isPickerOpenRef.current, publishLatestDraft)
  }

  const handlePickerClose = () => {
    isPickerOpenRef.current = false
    publishLatestDraft()
  }

  const handlePickerOpen = () => {
    isPickerOpenRef.current = true
  }

  return (
    <LocalizationProvider
      adapterLocale={i18n.language}
      dateAdapter={AdapterLuxon}
    >
      <DateRangePicker
        calendars={2}
        maxDate={pickerBounds.maxDate ?? undefined}
        minDate={pickerBounds.minDate ?? undefined}
        onChange={handleDateRangeChange}
        onClose={handlePickerClose}
        onOpen={handlePickerOpen}
        slotProps={{
          field: {
            onBlur: handleFieldBlur,
            onKeyDown: handleFieldKeyDown,
          },
          shortcuts: {
            items: shortcuts.map((shortcut) => ({
              disabled: shortcut.disabled,
              getValue: () => shortcut.getValue(),
              label: shortcut.label,
            })),
          },
          textField: {
            label: t("intervalData.dateRange"),
            size: "small",
          },
        }}
        sx={{ width: 256 }}
        value={pickerValue}
      />
    </LocalizationProvider>
  )
}
